import { ClickAwayListener, Popper } from '@material-ui/core'
import { orgSelectors } from 'ducks/orgs'
import SupplierLogoWithFallback from 'elements/SupplierLogoWithFallback'
import { Box, CircularProgress, EyeIcon, IconButton, styled, Tooltip, Typography, useTheme } from 'opensolar-ui'
import type LineItemType from 'pages/ordering/OrderLineItem'
import { useGetSupplierConfig } from 'pages/ordering/hooks/useGetSupplierConfig'
import type { HardwareOrderProjectRecord, HardwareSupplierFilterKeyType } from 'pages/ordering/type'
import { useCallback, useRef, useState } from 'react'
import { useTranslate } from 'react-admin'
import { useSelector } from 'react-redux'
import { formatCurrencyWithSymbol } from 'util/misc'
import type ProjectTablePresenter from '../projectTablePresenter'

const PopoverPaper = styled(Box)(({ theme }) => ({
  padding: '12px 16px',
  minWidth: 220,
  maxWidth: 320,
  backgroundColor: theme.palette.common.white,
  boxShadow: theme.shadows[4],
  borderRadius: 8,
  border: `1px solid ${theme.palette.grey[200]}`,
  position: 'relative',
}))

const PreviewRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  padding: '4px 0',
  fontSize: 12,
  color: theme.palette.grey[900],
}))

const CodeCell = styled(Typography)({
  flex: '1 1 auto',
  minWidth: 0,
  fontSize: 12,
  fontWeight: 500,
})

const QtyCell = styled(Typography)(({ theme }) => ({
  flex: '0 0 auto',
  fontSize: 12,
  color: theme.palette.grey[700],
}))

const DistributorIconWrap = styled(Box)({
  flex: '0 0 24px',
  width: 24,
  height: 24,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  '& img': {
    width: 20,
    height: 20,
    objectFit: 'contain',
  },
})

const TotalRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginTop: 8,
  paddingTop: 8,
  borderTop: `1px solid ${theme.palette.grey[200]}`,
  fontSize: 13,
  fontWeight: 600,
  color: theme.palette.grey[900],
}))

const LoadingWrap = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  minHeight: 60,
})

type SystemBomPreviewPopoverProps = {
  record: HardwareOrderProjectRecord
  selectedSystemId: number | undefined
  projectTablePresenter: ProjectTablePresenter
  disabled?: boolean
}

export const SystemBomPreviewPopover = ({
  record,
  selectedSystemId,
  projectTablePresenter,
  disabled = false,
}: SystemBomPreviewPopoverProps) => {
  const translate = useTranslate()
  const theme = useTheme()
  const { getSupplierConfig } = useGetSupplierConfig()
  const currencySymbol = useSelector(orgSelectors.getCurrencySymbolFromOrg)

  const [isOpen, setIsOpen] = useState(false)
  const [preview, setPreview] = useState<{ lineItems: LineItemType[]; subtotal: number } | null>(null)
  const [loading, setLoading] = useState(false)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const canOpen = !disabled && selectedSystemId != null
  const isEyeDisabled = disabled || selectedSystemId == null

  const loadPreview = useCallback(async () => {
    if (!selectedSystemId) return
    setLoading(true)
    setPreview(null)
    try {
      const result = await projectTablePresenter.getPreviewLineItemsForSystem({
        systemsDesign: record.systems,
        systemId: selectedSystemId,
        projectId: record.id,
      })
      setPreview(result)
    } finally {
      setLoading(false)
    }
  }, [record.systems, record.id, selectedSystemId, projectTablePresenter])

  const closeImmediately = useCallback(() => {
    setIsOpen(false)
    setPreview(null)
  }, [])

  const handleIconMouseEnter = useCallback(
    (e: React.MouseEvent) => {
      if (!canOpen) return
      e.preventDefault()
      e.stopPropagation()
      setIsOpen(true)
      loadPreview()
    },
    [canOpen, loadPreview]
  )

  const handleClickAway = useCallback(
    (e: React.MouseEvent<Document> | React.TouchEvent<Document>) => {
      const target = e.target as Node
      if (triggerRef.current?.contains(target)) return
      closeImmediately()
    },
    [closeImmediately]
  )

  const renderDistributorIcon = (lineItem: LineItemType) => {
    const dist = lineItem.selectedDistributor
    if (!dist || dist === 'exclude' || dist === 'component') {
      return (
        <DistributorIconWrap title={translate('Exclude from order')}>
          <span style={{ fontSize: 10, color: theme.palette.grey[500] }}>–</span>
        </DistributorIconWrap>
      )
    }
    const config = getSupplierConfig(dist as HardwareSupplierFilterKeyType)
    if (!config?.logoUrl) {
      return (
        <DistributorIconWrap title={config?.name ?? dist}>
          <img
            src={`${window.PUBLIC_URL}/images/supplier_placeholder.png`}
            alt={config?.name ?? dist}
            style={{ width: 20, height: 20, objectFit: 'contain' }}
          />
        </DistributorIconWrap>
      )
    }
    return (
      <DistributorIconWrap title={config.name}>
        <SupplierLogoWithFallback logoUrl={config.logoUrl} name={config.name} />
      </DistributorIconWrap>
    )
  }

  return (
    <div style={{ display: 'inline-flex' }} onMouseLeave={closeImmediately}>
      <Box component="span" onMouseEnter={handleIconMouseEnter} sx={{ display: 'inline-flex' }}>
        <Tooltip title={isEyeDisabled ? translate('Select a system to preview BOM') : ''}>
          <span>
            <IconButton
              ref={triggerRef}
              size="small"
              disabled={isEyeDisabled}
              sx={{
                color: isEyeDisabled ? theme.palette.grey[400] : theme.palette.grey[700],
              }}
              aria-label={translate('BOM summary')}
            >
              <EyeIcon size={20} />
            </IconButton>
          </span>
        </Tooltip>
      </Box>
      <ClickAwayListener onClickAway={handleClickAway} mouseEvent="onMouseDown" touchEvent="onTouchStart">
        <Popper
          open={isOpen}
          anchorEl={triggerRef.current}
          placement="bottom-end"
          style={{ zIndex: 10000 }}
          modifiers={{
            offset: { enabled: true, offset: '0, 8' },
            preventOverflow: { enabled: true, boundariesElement: 'viewport' },
          }}
        >
          <PopoverPaper>
            {loading ? (
              <LoadingWrap>
                <CircularProgress size={24} />
              </LoadingWrap>
            ) : preview ? (
              <>
                {preview.lineItems.length === 0 ? (
                  <Typography variant="body2" color="textSecondary">
                    {translate('No components in design.')}
                  </Typography>
                ) : (
                  <>
                    {preview.lineItems.map((li, index) => (
                      <PreviewRow key={`${li.code}-${li.quantity}-${index}`}>
                        <CodeCell variant="body2">{li.code}</CodeCell>
                        <QtyCell variant="body2">×{li.quantity}</QtyCell>
                        {renderDistributorIcon(li)}
                      </PreviewRow>
                    ))}
                    <TotalRow>
                      <span>{translate('Subtotal')}</span>
                      <span>{formatCurrencyWithSymbol(preview.subtotal, currencySymbol, undefined, 2)}</span>
                    </TotalRow>
                  </>
                )}
              </>
            ) : null}
          </PopoverPaper>
        </Popper>
      </ClickAwayListener>
    </div>
  )
}
